import { useState } from "react";
import { Phone, Mail, MapPin, Calendar, Send, CheckCircle2, Briefcase, LifeBuoy, Settings2, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { useBookDemo } from "@/context/BookDemoContext";

const AREAS = [
    { icon: Briefcase, title: "Demo & salg", desc: "Skal vi vise jer flowet, eller har I konkrete spørgsmål til priser?" },
    { icon: LifeBuoy, title: "Support", desc: "Eksisterende kunder kan kontakte os for hurtig hjælp i hverdagen." },
    { icon: Settings2, title: "Opsætning", desc: "Vi hjælper jer i gang og tilpasser flowet til jeres arbejdsdag." },
    { icon: Handshake, title: "Samarbejde", desc: "Partner, integration eller andet — vi tager gerne en snak." },
];

const initialForm = { name: "", company: "", email: "", phone: "", message: "" };

export default function Kontakt() {
    const { open: openDemo } = useBookDemo();
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const handleChange = (e) => {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Udfyld venligst navn, email og besked.");
            return;
        }
        setSubmitting(true);
        try {
            await submitContact(form);
            setSent(true);
            setForm(initialForm);
            toast.success("Tak — vi vender tilbage hurtigst muligt.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Noget gik galt. Prøv igen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-kontakt">
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute -top-20 -left-32 w-[420px] h-[420px] rounded-full bg-blue-200/30 blur-3xl pointer-events-none" />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wider border border-blue-100">
                        Kontakt
                    </span>
                    <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                        Kontakt{" "}
                        <span className="gradient-text">teamet.</span>
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        Har du spørgsmål om demo, opsætning, support eller hvordan MyServiceHelper passer til din virksomhed? Så skriv eller ring til os.
                    </p>
                </div>
            </section>

            <section className="pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                        {/* Contact info */}
                        <div className="lg:col-span-2 space-y-4">
                            <div className="rounded-2xl bg-white border border-slate-200 p-6">
                                <h3 className="text-base font-semibold text-slate-900">Kontaktoplysninger</h3>
                                <ul className="mt-5 space-y-4">
                                    <li className="flex items-start gap-3">
                                        <span className="shrink-0 w-10 h-10 rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 inline-flex items-center justify-center">
                                            <Phone className="w-4 h-4" />
                                        </span>
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Telefon</p>
                                            <a href="tel:60566805" data-testid="contact-phone" className="text-slate-900 hover:text-blue-600 font-medium">60 56 68 05</a>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="shrink-0 w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100 inline-flex items-center justify-center">
                                            <Mail className="w-4 h-4" />
                                        </span>
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</p>
                                            <a href="mailto:Xenius@Gregersen.dk" data-testid="contact-email" className="text-slate-900 hover:text-blue-600 font-medium break-all">
                                                Xenius@Gregersen.dk
                                            </a>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="shrink-0 w-10 h-10 rounded-xl bg-purple-50 text-purple-600 ring-1 ring-purple-100 inline-flex items-center justify-center">
                                            <MapPin className="w-4 h-4" />
                                        </span>
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Adresse</p>
                                            <p className="text-slate-900 font-medium">Vibevænget 3, 5700 Svendborg</p>
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 text-white p-6 shadow-lg shadow-blue-500/20">
                                <h3 className="text-lg font-semibold">Book gratis demo</h3>
                                <p className="mt-2 text-sm text-white/85">
                                    Vil du se MyServiceHelper i praksis? Book en gratis demo, så gennemgår vi flowet fra kundehenvendelse til færdig opgave.
                                </p>
                                <a href="#kontakt-form" onClick={(e) => { e.preventDefault(); openDemo(); }}>
                                    <Button data-testid="kontakt-book-demo-btn" className="mt-5 w-full rounded-full h-11 bg-white text-slate-900 hover:bg-slate-100">
                                        <Calendar className="w-4 h-4 mr-2" />
                                        Book gratis demo
                                    </Button>
                                </a>
                            </div>
                        </div>

                        {/* Form */}
                        <div id="kontakt-form" className="lg:col-span-3">
                            <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm">
                                {sent ? (
                                    <div data-testid="contact-success" className="py-10 text-center">
                                        <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 inline-flex items-center justify-center">
                                            <CheckCircle2 className="w-7 h-7" />
                                        </div>
                                        <h3 className="mt-5 text-2xl font-bold text-slate-900">Tak for din besked</h3>
                                        <p className="mt-2 text-slate-600 max-w-md mx-auto">
                                            Vi har modtaget din henvendelse og vender tilbage hurtigst muligt.
                                        </p>
                                        <Button
                                            data-testid="contact-send-another"
                                            onClick={() => setSent(false)}
                                            variant="outline"
                                            className="mt-6 rounded-full h-11 px-5 border-slate-300"
                                        >
                                            Send en ny besked
                                        </Button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} noValidate className="space-y-5" data-testid="contact-form">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                            <div className="space-y-1.5">
                                                <Label htmlFor="name" className="text-sm font-medium text-slate-700">Navn *</Label>
                                                <Input
                                                    id="name" name="name" value={form.name} onChange={handleChange}
                                                    placeholder="Dit navn"
                                                    data-testid="contact-input-name"
                                                    className="h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-blue-500/30"
                                                    required
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <Label htmlFor="company" className="text-sm font-medium text-slate-700">Virksomhed</Label>
                                                <Input
                                                    id="company" name="company" value={form.company} onChange={handleChange}
                                                    placeholder="Virksomhedsnavn"
                                                    data-testid="contact-input-company"
                                                    className="h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-blue-500/30"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email *</Label>
                                                <Input
                                                    id="email" name="email" type="email" value={form.email} onChange={handleChange}
                                                    placeholder="din@email.dk"
                                                    data-testid="contact-input-email"
                                                    className="h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-blue-500/30"
                                                    required
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Telefon</Label>
                                                <Input
                                                    id="phone" name="phone" value={form.phone} onChange={handleChange}
                                                    placeholder="Dit telefonnummer"
                                                    data-testid="contact-input-phone"
                                                    className="h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-blue-500/30"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label htmlFor="message" className="text-sm font-medium text-slate-700">Besked *</Label>
                                            <Textarea
                                                id="message" name="message" value={form.message} onChange={handleChange}
                                                placeholder="Skriv kort om jeres behov…"
                                                rows={5}
                                                data-testid="contact-input-message"
                                                className="rounded-xl bg-white border-slate-200 focus-visible:ring-blue-500/30"
                                                required
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="contact-submit-btn"
                                            className="w-full sm:w-auto rounded-full h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md disabled:opacity-60"
                                        >
                                            <Send className="w-4 h-4 mr-2" />
                                            {submitting ? "Sender…" : "Send besked"}
                                        </Button>
                                        <p className="text-xs text-slate-500">
                                            Ved at sende beskeden accepterer du, at vi må kontakte dig om din henvendelse.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Areas */}
            <section className="pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wider border border-blue-100">
                            Kontaktområder
                        </span>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900">
                            Hvad kan vi hjælpe med?
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {AREAS.map((a, i) => {
                            const Icon = a.icon;
                            return (
                                <div key={i} data-testid={`area-card-${i}`} className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-md transition-all">
                                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 ring-1 ring-blue-100 inline-flex items-center justify-center">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="mt-4 text-base font-semibold text-slate-900">{a.title}</h3>
                                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}
